import React from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import BottleLabel from './BottleLabel';
import { useCart } from '../context/CartContext';
import { rememberHomeScroll } from './ScrollToTop';
import { isOutOfStock } from '../lib/stock';

export default function ProductCard({ product, compact = false, sectionId }) {
  const { addItem } = useCart();
  const discount = Math.round(100 - (product.price / product.mrp) * 100);
  const remember = () => rememberHomeScroll(sectionId);
  const soldOut = isOutOfStock(product);

  return (
    <div className="group relative bg-white/70 backdrop-blur-sm rounded-md overflow-hidden border border-[#E5DCC9] hover:border-[#C9A961] hover:shadow-xl transition-all duration-500 h-full flex flex-col">
      {product.badge && !soldOut && (
        <span className="absolute top-2.5 left-2.5 z-10 bg-[#1A2332] text-[#F5EFE6] text-[8px] sm:text-[9px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-sm font-medium max-w-[70%] truncate">
          {product.badge}
        </span>
      )}
      {soldOut && (
        <span className="absolute top-2.5 left-2.5 z-10 bg-[#1A2332] text-[#F5EFE6] text-[8px] sm:text-[9px] tracking-[0.2em] uppercase px-2.5 py-1 rounded-sm font-medium">
          Out of stock
        </span>
      )}
      <Link
        to={`/products/${product.slug}`}
        onClick={remember}
        className="block relative overflow-hidden"
        style={{
          background: `radial-gradient(600px 200px at 50% 100%, ${product.accent}22, transparent 60%), linear-gradient(180deg, #FBF6EC 0%, #F0E6D3 100%)`,
        }}
      >
        <div className="w-full aspect-square group-hover:scale-[1.06] transition-transform duration-700 ease-out">
          <BottleLabel product={product} size="fill" />
        </div>
        <div className="absolute top-2 right-2 w-4 h-4 border-t border-r border-[#C9A961] opacity-0 group-hover:opacity-100 transition-opacity"></div>
        <div className="absolute bottom-2 left-2 w-4 h-4 border-b border-l border-[#C9A961] opacity-0 group-hover:opacity-100 transition-opacity"></div>
      </Link>
      <div className="p-3.5 sm:p-4 flex flex-col flex-1">
        <div className="flex items-center gap-1 text-[11px] sm:text-xs text-[#1A2332]/70 mb-1">
          <Star className="w-3.5 h-3.5 fill-[#C9A961] text-[#C9A961] flex-shrink-0" />
          <span>{product.rating}</span>
          <span className="opacity-50 truncate">| {product.reviews} Reviews</span>
        </div>
        <Link to={`/products/${product.slug}`} onClick={remember}>
          <h3 className="font-serif-display text-[16px] sm:text-[17px] leading-snug text-[#1A2332] hover:underline">
            {product.name}
          </h3>
        </Link>
        <div className="flex flex-wrap gap-1 mt-1 mb-2">
          <span className="text-[9px] tracking-[0.15em] uppercase text-[#1A2332]/60">{product.type}</span>
          <span className="text-[9px] tracking-[0.15em] uppercase text-[#1A2332]/60">• {product.gender}</span>
          <span className="text-[9px] tracking-[0.15em] uppercase text-[#1A2332]/60">• {product.family}</span>
          <span className="text-[9px] tracking-[0.15em] uppercase text-[#1A2332]/60">• 50 ML</span>
        </div>
        <div className="flex items-baseline gap-2 mb-3 flex-wrap">
          <span className="font-serif-display text-lg text-[#1A2332]">₹{product.price}</span>
          <span className="text-sm line-through text-[#1A2332]/40">₹{product.mrp}</span>
          <span className="text-[10px] font-semibold text-[#8a4a2b] sm:ml-auto">{discount}% OFF</span>
        </div>
        <button
          type="button"
          disabled={soldOut}
          onClick={() => !soldOut && addItem(product, 1)}
          className="mt-auto w-full bg-[#1A2332] text-[#F5EFE6] py-2.5 text-[10px] sm:text-[11px] tracking-[0.15em] sm:tracking-[0.2em] uppercase hover:bg-[#2c3e5b] transition-colors rounded-sm touch-manipulation disabled:opacity-40 disabled:hover:bg-[#1A2332]"
        >
          {soldOut ? 'Out of stock' : 'Add to Cart'}
        </button>
      </div>
    </div>
  );
}
